import {
  useCallback,
  useEffect,
  useState,
  FC,
  useMemo,
  memo,
  useRef,
  ReactElement,
  ReactNode,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { ENV } from 'shared/ENV';
import {
  MapComponent,
  Map as GLMap,
  MarkerComponent,
  useMap,
  LngLatBoundsLike,
  LngLat,
  LngLatBounds,
  FitBoundsOptions,
  ScaleControl,
  AttributionControl,
  ViewState,
  PointLike,
  MarkerClickEvent,
} from 'shared/map-exports';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  useMediaQuery,
  Typography,
  useTheme,
  ButtonGroup,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useUserPosition } from 'hooks/useUserPosition';
import { usePoisState } from 'state/usePoisState';
import { usePlaces } from 'features/places/hooks/usePlaces';
import { MapLocation } from 'shared/types';
import {
  initialMapLayersState,
  useMapLayersState,
} from 'state/useMapLayersState';
import { useWebGL2State } from 'state/useWebGL2State';
import { useSelectedLocationState } from 'state/useSelectedLocationState';
import MapBoxLogo from 'assets/mapbox.svg?react';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { useAllowMapLongPress } from 'hooks/useMapLongPress';
import { useDebounceCallback, useResizeObserver } from 'usehooks-ts';
import { StoreDebugContextMapRef } from 'hooks/useCaptureDebugData/StoreDebugContextMapRef';
import { useAuthState } from 'state/useAuthState';
import {
  useGeoLocationCommandPaletteAction,
  useMapLayerCommandPaletteActions,
  useTimelineCommandPaletteActions,
} from 'hooks/commandPaletteActions';
import { useOnPixelRatioChange } from 'hooks/useOnPixelRatioChange';
import { useMapBearing } from 'hooks/useMapBearing';
import { useMapMode } from 'hooks/useMapMode';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { useRecenterMapOnRotation } from 'features/mapping/hooks/useRecenterMapOnRotation';

import { hasValidCoordinates, isIframe } from 'shared/utils';
import {
  MapProps,
  ReportMediaMarkersProps,
  ReportMarkerProps,
  MarkerProps,
  MapEventsProps,
  UserMarkerProps,
  PlaceMarkerProps,
  LocationMarkerProps,
  WildfireGeoEventMarkerProps,
  FloodingGeoEventMarkerProps,
  HazardGeoEventMarkerProps,
} from 'features/mapping/mapping.types';
import { LayersControl } from 'features/mapping/components/controls/LayersControl';
import { GeolocateControl } from 'features/mapping/components/controls/GeolocateControl';
import { NavigationControl } from 'features/mapping/components/controls/NavigationControl';
import { MapViewDimensionControl } from 'features/mapping/components/controls/MapViewDimensionControl';
import { LegendControl } from 'features/mapping/components/controls/LegendControl';
import { MapToolsControl } from 'features/mapping/components/controls/MapToolsControl';
import { CompassControl } from 'features/mapping/components/controls/CompassControl';
import { TimelineModeControl } from 'features/mapping/components/controls/TimelineModeControl';
import { LayersDrawer } from 'features/mapping/components/controls/LayersDrawer';
import { LayersGroupDrawer } from 'features/mapping/components/controls/LayersGroupDrawer';
import { PoiDialog } from 'features/mapping/components/dialogs/PoiDialog';
import { AppStoresDialog } from 'features/mapping/components/dialogs/AppStoresDialog';
import { SupportUsDialog } from 'features/mapping/components/dialogs/SupportUsDialog';
import { LayersNavControl } from 'features/mapping/components/dialogs/LayersNavControl';
import {
  AircraftsLayer,
  AircraftsLayerDeckGl,
} from 'features/mapping/components/layers/AircraftsLayer';
import { FederalLandsLayer } from 'features/mapping/components/layers/FederalLandsLayer';
import { ElectricRetailServiceLayer } from 'features/mapping/components/layers/ElectricRetailServiceLayer';
import { ResponsibilityAreasLayer } from 'features/mapping/components/layers/ResponsibilityAreasLayer';
import { StructuredEvacuationsAllLayer } from 'features/mapping/components/layers/StructuredEvacuationsLayer';
import { ViirsLayer } from 'features/mapping/components/layers/ViirsLayer';
import { ModisLayer } from 'features/mapping/components/layers/ModisLayer';
import {
  HeatDetectionsLayer,
  getEnabledSatellites,
} from 'features/mapping/components/layers/HeatDetectionsLayer';
import { NgfsDetectionsLayer } from 'features/mapping/components/layers/NgfsDetectionsLayer';
import { PurpleAirLayer } from 'features/mapping/components/layers/PurpleAirLayer';
import { AttributionLayer } from 'features/mapping/components/layers/AttributionLayer';
import { WindLayer } from 'features/mapping/components/layers/WindLayer';
import { DeckOverlay } from 'features/mapping/components/DeckOverlay';
import { PlacesLayer } from 'features/mapping/components/layers/PlacesLayer';
import { RadioTowersLayer } from 'features/mapping/components/layers/RadioTowersLayer';
import { ExternalGeoEventsLayer } from 'features/mapping/components/layers/ExternalGeoEventsLayer';
import { NWSWarningsAndAlertsLayer } from 'features/mapping/components/layers/NWSWatchesAndWarningsLayer';
import {
  ActiveFirePerimetersLayer,
  HistoricalFirePerimetersLayer,
} from 'features/mapping/components/layers/FirePerimetersLayer';
import { ReporterCoverageLayer } from 'features/mapping/components/layers/ReporterCoverageLayer';
import { RadarCoverageLayer } from 'features/mapping/components/layers/RadarCoverageLayer';
import { RegionsLayer } from 'features/mapping/components/layers/RegionsLayer';
import { ReportObfuscationLayer } from 'features/mapping/components/layers/ReportObfuscationLayer';
import { useLayersDrawerState } from 'features/mapping/hooks/useLayersDrawerState';
import { useLocationV2Enabled } from 'features/locations/hooks/useLocationV2Enabled';
import { MAP_ENTITY_DRAWER_WIDTH } from 'features/mapping/components/MapEntityDrawer';
import { useMapEntityDrawerState } from 'features/mapping/hooks/useMapEntityDrawerState';
import { SearchBar } from 'features/mapping/components/SearchBar';
import { LocationPermissionDialog } from 'features/mapping/components/dialogs/LocationPermissionDialog';
import {
  AllMapLayers,
  DEFAULT_3D_EXAGGERATION,
  DEFAULT_LATLNG,
  DEFAULT_3D_PITCH,
  DEFAULT_TIMELINE_LAYER_ID,
  GEO_EVENT_MARKER_Z_INDEX,
  LAYERS_DRAWER_WIDTH,
  LOCATION_ZOOM_LEVEL,
  MapBaseLayerStyles,
  MapLayer,
  MapLayers,
  MapLayersDetails,
  TOP_MARKER_Z_INDEX,
  DEFAULT_VIEW_STATE,
  DefaultViewState,
  ZOOM_LEVELS,
} from 'features/mapping/mapping.constants';
import { MapType, MAP_TYPE_POLICIES } from 'features/mapping/mapTypePolicy';
import {
  canUserAccessMapLayer,
  hasWebGL2Support,
} from 'features/mapping/mapping.utils';
import {
  getOpacityForReportMarker,
  ReportIcon,
  ReportIconFov,
  CurrentLocationIcon,
  getOpacityForGeoEventMarker,
  PlaceLocationPinIcon,
  LocationPinIcon,
  getLocationIcon,
  getFloodingEventScale,
  getFloodingEventIcon,
  getWildfireEventScale,
  getWildfireGeoEventIcon,
  getHazardEventIcon,
} from 'features/mapping/icons';
import { useAuthenticatedTileRequestTransformer } from 'features/mapping/hooks/useAuthenticatedTileRequestTransformer';
import { useHandleMissingMapStyleImage } from 'features/mapping/hooks/useHandleMissingMapStyleImage';
import { useContextMenu } from 'hooks/useContextMenu';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { getMapIconsWithRasters } from 'features/mapping/icons/mapIconsWithRasters';
import { WILDFIRE_MAP_RASTER_ICON_NAMES } from 'features/mapping/icons/wildfireIcons.utils';
import { LocationMarkerLabel } from 'features/mapping/components/LocationMarkerLabel';
import { TIMELINE_MOBILE_LEGEND_HEIGHT } from 'features/mapping/components/timeline/timeline.constants';
import { useTimelineLayerToggle } from 'features/mapping/hooks/useTimelineLayerToggle';
import { MeasureDistanceToolLayer } from 'features/mapping/components/layers/MeasureDistanceToolLayer';
import {
  ContextMenuItemKey,
  MapToolsContextMenu,
} from 'features/mapping/components/MapToolsContextMenu';
import { PlanetLabsBasemapLayer } from 'features/planetLabs/components/PlanetLabsBasemapLayer';
import { useLightningLayerEnabled } from 'features/mapping/hooks/useLightningLayerEnabled/useLightningLayerEnabled';
import { TimelineLayers } from 'features/mapping/components/timeline/TimelineLayers';
import { MapContextProvider } from './MapContext';
import {
  MAP_DOM_ELEMENT_ID,
  getSearchCardMinSnapPx,
  SEARCH_DRAWER_WIDTH,
  RESIZE_DEBOUNCE_MS,
  STATUS_ALERT_OFFSET_CSS_VALUE,
} from '../../../../constants';
import { ZoomLevelControl } from '../controls/ZoomLevelControl';
import { PowerOutageLayer } from '../layers/PowerOutageLayer';
import { useMapClickHandler } from '../../mapClick';
import { FemaFloodHazardLayer } from '../layers/FemaFloodHazardLayer';
import { LightningLayer } from '../layers/LightningLayer';

declare global {
  interface Window {
    wdMapLoaded?: boolean;
  }
}

const useStyles = makeStyles<{
  showSearch?: boolean;
  hasSearchBarAdornment?: boolean;
}>()((theme, { showSearch, hasSearchBarAdornment }) => {
  const narrowContainer = `@container (max-width: ${theme.breakpoints.values.sm}px)`;
  return {
    content: {
      width: '100%',
      height: '100%',
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      // We have 2 layer drawers, so we need to increase negative margin accordingly
      // to avoid overlapping the map even when they are closed (white space).
      marginRight: -LAYERS_DRAWER_WIDTH * 2,
      position: 'relative',
      '& .mapboxgl-ctrl-top-right, & .mapboxgl-ctrl-bottom-right': {
        transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      },
      '& .mapboxgl-ctrl-top-left': {
        top: showSearch ? 48 : 0,
      },
      '& .mapboxgl-ctrl-top-right': {
        top: 0,
        [narrowContainer]: {
          top: showSearch && hasSearchBarAdornment ? 48 : 0,
        },
      },
    },
    contentWithSideDrawerOpen: {
      [theme.breakpoints.up('tablet')]: {
        '& .mapboxgl-ctrl-top-right, & .mapboxgl-ctrl-bottom-right': {
          transform: `translateX(-${
            MAP_ENTITY_DRAWER_WIDTH - SEARCH_DRAWER_WIDTH
          }px)`,
        },
      },
    },
    contentWithSideDrawerClosed: {
      [theme.breakpoints.up('tablet')]: {
        '& .mapboxgl-ctrl-top-right, & .mapboxgl-ctrl-bottom-right': {
          transform: `translateX(-${MAP_ENTITY_DRAWER_WIDTH}px)`,
        },
      },
    },
    mobileContent: {
      width: '100%',
      height: '100%',
      position: 'relative',
      '& .mapboxgl-ctrl-top-left': {
        top: showSearch ? 48 : 0,
      },
      '& .mapboxgl-ctrl-top-right': {
        top: 0,
        [narrowContainer]: {
          top: showSearch && hasSearchBarAdornment ? 48 : 0,
        },
      },
    },
    // `container-type: inline-size` makes this a CSS container-query context, so
    // map UI (control offsets, the search bar) can respond to the map's own width
    // via `@container` rather than the viewport width. It sits on this inner
    // element and NOT on `#map-container` because iPadOS 17 WebKit treats
    // `container-type` as a containing block for `position: fixed` descendants,
    // which would trap fixed overlays portaled into `#map-container` (e.g. the
    // MapEntityDrawer) inside the map instead of letting them cover the viewport.
    containerQueryContext: {
      containerType: 'inline-size',
      width: '100%',
      height: '100%',
    },
    locationLabelContainer: {
      position: 'absolute',
      top: '100%',
      minWidth: 'max-content',
      maxWidth: 300,
    },
    placeIcon: {
      position: 'absolute',
      top: '19%',
      left: 'calc(50% - 8px)',
      color: 'white',
      path: 'white',
      width: 16,
      height: 16,
      '& path, rect': {
        fill: 'white',
      },
    },
    placeLabel: {
      textShadow:
        '2px 0 #fff, -2px 0 #fff, 0 2px #fff, 0 -2px #fff, 1px 1px #fff, -1px -1px #fff, 1px -1px #fff, -1px 1px #fff',
      fontWeight: 600,
    },
    markerContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      // Prevents a long-press on the marker image from opening the context menu on mobile devices.
      WebkitTouchCallout: 'none',
    },
  };
});

export const Marker = (props: MarkerProps): JSX.Element => {
  const {
    icon,
    onClick,
    opacity = 1,
    position,
    anchor = 'center',
    rotation = 0,
    scale = 1,
    draggable,
    onDrag,
    onDragStart,
    onDragEnd,
    zIndex,
    label,
  } = props;
  const { classes } = useStyles({});
  const { lat, lng } = position;
  const setAllowLongPress = useAllowMapLongPress();

  const isClickable = Boolean(onClick);

  let markerCursor: string;
  if (isClickable) {
    markerCursor = 'pointer';
  } else if (draggable) {
    markerCursor = 'grab';
  } else {
    markerCursor = 'auto';
  }

  const iconScale = icon.scale || 1.0;
  const finalScale = scale * iconScale;
  const offset: PointLike | undefined = icon.offset
    ? [icon.offset[0] * finalScale, icon.offset[1] * finalScale]
    : undefined;

  const imgWidth = icon.width * finalScale;
  const imgHeight = icon.height * finalScale;

  const handleClick = (e: MarkerClickEvent): void => {
    if (isClickable && onClick) {
      onClick(e);
    }
  };

  return (
    <MarkerComponent
      latitude={lat}
      longitude={lng}
      anchor={anchor}
      rotation={rotation}
      onClick={handleClick}
      offset={offset}
      draggable={draggable}
      onDrag={onDrag}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{ cursor: markerCursor, zIndex }}
    >
      <Box className={classes.markerContainer}>
        <div
          // Disable long press listener for the map while the user
          // is interacting with this marker.
          onTouchStart={() => draggable && setAllowLongPress(false)}
          onTouchEnd={() => draggable && setAllowLongPress(true)}
        >
          <Box sx={{ position: 'relative' }}>
            <img
              src={icon.data}
              alt="marker icon"
              style={{
                opacity,
                width: imgWidth,
                height: imgHeight,
              }}
            />
          </Box>
          {Boolean(label) && (
            <div className={classes.locationLabelContainer}>
              {typeof label === 'string' ? (
                <Typography color="textPrimary" className={classes.placeLabel}>
                  {label}
                </Typography>
              ) : (
                label
              )}
            </div>
          )}
        </div>
      </Box>
    </MarkerComponent>
  );
};

const UserMarker = (props: UserMarkerProps): JSX.Element => {
  const { lat, lng, heading } = props;

  return (
    <Marker
      position={{ lat, lng }}
      icon={CurrentLocationIcon}
      rotation={heading}
      scale={0.6}
      zIndex={TOP_MARKER_Z_INDEX}
    />
  );
};

// @todo remove PlaceMarker when removing 'pin_drop_improvements' release flag
const PlaceMarker = (props: PlaceMarkerProps): JSX.Element => {
  const { id, lat, lng, name, placeIcon, onClick } = props;

  const icon = placeIcon ? PlaceLocationPinIcon : LocationPinIcon;

  const getLabel = (): string | JSX.Element | undefined => {
    if (id) {
      return undefined; // Saved places labels are rendered by mapbox gl
    }
    if (!placeIcon) {
      return <LocationMarkerLabel label={name} />;
    }
    return name;
  };

  return (
    <Marker
      position={{ lat, lng }}
      icon={icon}
      scale={1}
      zIndex={TOP_MARKER_Z_INDEX}
      label={getLabel()}
      onClick={onClick}
    />
  );
};

const LocationMarker = (props: LocationMarkerProps): JSX.Element | null => {
  const { location, draggable, onDrag, onDragEnd, scale, opacityOverride } =
    props;
  const showLocationV2 = useLocationV2Enabled();

  if (!hasValidCoordinates(location) || !location.data?.locationType) {
    return null;
  }

  return (
    <Marker
      zIndex={GEO_EVENT_MARKER_Z_INDEX}
      scale={scale}
      opacity={opacityOverride}
      position={{ lat: location.lat, lng: location.lng }}
      anchor="bottom"
      icon={getLocationIcon(location.data.locationType, showLocationV2)}
      draggable={draggable}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
    />
  );
};

const Map = (props: MapProps): JSX.Element => {
  const {
    children,
    // id cannot be 'default' if the map is a child map used within a mapProvider context. See: https://trello.com/c/qwh1L8gg
    id = 'default',
    mapType = MapType.Main,
    center = DEFAULT_LATLNG,
    zoom = ZOOM_LEVELS.CITY,
    minZoom = DEFAULT_VIEW_STATE.minZoom,
    cursor = 'auto',
    style = {},
    showSearch = false,
    searchDisabled = false,
    searchIgnoresSelectedGeoEvent = false,
    searchBarAdornment,
    onViewportChange,
    interactive = true,
    onLocationChange,
    searchApi,
    onClick,
    onContextMenu,
    onTouchStart,
    bounds,
    fitBoundsOptions,
    withSearchDrawer = false,
  } = props;
  const userPosition = useUserPosition({ pollingEnabled: true });
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('phone'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('tablet'));
  const { state: locationState } = useLocation<MapLocation | undefined>();
  // Use state to hold the map reference instead of useRef(). State allows
  // hooks below to be re-run when the map reference becomes available (or changes).
  const [map, setMap] = useState<GLMap | undefined>();
  const mapLayersState = useMapLayersState();
  const { is3D, mapBaseLayer } = mapLayersState;
  const mapStyle = MapBaseLayerStyles[mapBaseLayer];
  const { places } = usePlaces();
  const { selectedLocation, setSelectedLocation } = useSelectedLocationState();
  const { clearSelectedPois } = usePoisState();
  const { permissions, showMembershipProFeatures, showMembershipFeatures } =
    useAuthState();
  const { isInternalUser } = permissions;
  const pinImprovements = useReleaseFlag('pin_drop_improvements');
  const [cursorStyle, setCursorStyle] = useState(cursor);
  const { open: searchDrawerOpen } = useSearchDrawerState();
  const mapContainerElementRef = useRef<HTMLDivElement | null>(null);
  const { open: isDrawerEntitySelected } = useMapEntityDrawerState();
  const { openLayersDrawer } = useLayersDrawerState();
  const { showLightningLayer } = useLightningLayerEnabled();
  const isHeatDetectionsGisEnabled = useReleaseFlag(
    'show_heat_detections_from_gis_in_frontend',
  );
  const isNgfsDetectionsEnabled = useReleaseFlag(
    'show_ngfs_detections_in_frontend',
  );
  const isDeckGlAircraftLayerEnabled = useReleaseFlag(
    'aircraft_layer_use_deck_gl',
  );
  const unstructuredEvacuationsEnabled = useReleaseFlag(
    'unstructured_evacuations',
  );
  const { mapMode, isTimelineMode, setMapMode } = useMapMode();
  const isRadarActive = mapMode === 'radar';
  const { classes } = useStyles({
    showSearch,
    hasSearchBarAdornment: Boolean(searchBarAdornment),
  });

  useEffect(() => setCursorStyle(cursor), [cursor]);

  const {
    ctxMenuClose: formCtxMenuClose,
    contextMenuCoordinates: formContextMenuCoordinates,
    openContextMenu: formOpenContextMenu,
  } = useContextMenu();

  const handleTimelineLayerToggle = useTimelineLayerToggle();

  const handleTimelineToggle = useCallback(
    () => handleTimelineLayerToggle(DEFAULT_TIMELINE_LAYER_ID),
    [handleTimelineLayerToggle],
  );

  const handleTimelineClose = useCallback(() => {
    setMapMode('default');
  }, [setMapMode]);

  const handleMapClick = useMapClickHandler(onClick);

  const onContainerResize = useCallback((): void => {
    if (map) {
      map.resize();
    }
  }, [map]);
  const onDebouncedContainerResize = useDebounceCallback(
    onContainerResize,
    RESIZE_DEBOUNCE_MS,
  );

  useMapLayerCommandPaletteActions();

  useRecenterMapOnRotation(map);

  const { getUserPosition, setAskForPermission } = userPosition;

  const goToUserPosition = useCallback(async () => {
    const result = await getUserPosition();

    if (!result.locationEnabled) {
      setAskForPermission(true);
      return;
    }

    if (map && result.position && hasValidCoordinates(result.position)) {
      map.easeTo({
        center: [result.position.lng, result.position.lat],
        zoom: 13,
      });
    }
  }, [getUserPosition, map, setAskForPermission]);

  useGeoLocationCommandPaletteAction(goToUserPosition);

  const resetMapBearing = useCallback((): Promise<void> => {
    if (map) {
      map.easeTo({ bearing: 0 });
    }
    return Promise.resolve();
  }, [map]);

  // Manually refresh the map size when the map container size changes. Mapbox
  // only watches for window resize events automatically, but we need to watch
  // the container element specifically for a couple reasons (as of this writing):
  // - The search drawer on desktop opens/closes, changing the map container size
  // - iOS <=16 has an apparent bug where the window resize event isn't triggered
  //   when the keyboard is hidden, leaving a blank space where the keyboard was.
  // @see https://trello.com/c/NeSOPZrP/1929-collapsing-search-drawer-leaves-blank-space-on-map
  useResizeObserver({
    ref: mapContainerElementRef,
    onResize: onDebouncedContainerResize,
  });

  // initialize this hasWebGL2 globally into the state so downstate map components can check.
  //   Requires the map instance which we have here to compute.
  const { hasWebGL2, setEnabledData: setWebGL2Enabled } = useWebGL2State();

  useEffect(() => {
    if (hasWebGL2 !== undefined || !map) return;
    setWebGL2Enabled({ enabled: hasWebGL2Support(map) });
  }, [hasWebGL2, map, setWebGL2Enabled]);

  useEffect(() => {
    // De-select any selected POI when the map is unmounted
    return () => {
      clearSelectedPois();
    };
  }, [clearSelectedPois]);

  const { setDisabledLayers } = mapLayersState;
  const effectiveMapType = isTimelineMode ? MapType.Timeline : mapType;
  const policy = MAP_TYPE_POLICIES[effectiveMapType];

  // Gate on the base map type, not `policy` only, once timeline mode is active
  // effectiveMapType becomes MapType.Timeline, whose allowTimelineControl is
  // false and using `policy` here would unregister the shortcut and strand the
  // user in timeline mode
  useTimelineCommandPaletteActions(
    MAP_TYPE_POLICIES[mapType].allowTimelineControl,
    handleTimelineLayerToggle,
  );

  const providesOwnContextMenu = Boolean(onContextMenu);
  const toolsExclude: ContextMenuItemKey[] = providesOwnContextMenu
    ? ['copyPosition']
    : ['copyPosition', 'dropPin'];

  const fallbackContextMenuHandler = policy.allowToolsControl
    ? formOpenContextMenu
    : undefined;

  useEffect(() => {
    const disabledLayers = AllMapLayers.filter(
      (l: MapLayer) => !policy.allowedLayers.includes(l),
    );
    setDisabledLayers(disabledLayers);

    return () => {
      setDisabledLayers([]);
    };
  }, [setDisabledLayers, policy.allowedLayers]);

  const isLayerVisible = useCallback(
    (layer: MapLayer, options?: { policyKey?: MapLayer }): boolean => {
      const effectivePolicyKey = options?.policyKey ?? layer;
      if (!policy.allowedLayers.includes(effectivePolicyKey)) return false;
      if (
        !canUserAccessMapLayer({
          minMembership: MapLayersDetails[effectivePolicyKey]?.minMembership,
          requiredPermissions:
            MapLayersDetails[effectivePolicyKey]?.requiredPermissions,
          showMembershipFeatures,
          showMembershipProFeatures,
          permissions,
        })
      )
        return false;
      // Layers not in initialMapLayersState have no user toggle —
      // they're always visible when the policy allows them.
      if (!(effectivePolicyKey in initialMapLayersState)) return true;
      return mapLayersState.mapLayers.includes(layer);
    },
    [
      policy.allowedLayers,
      mapLayersState.mapLayers,
      permissions,
      showMembershipFeatures,
      showMembershipProFeatures,
    ],
  );

  // In timeline mode lightning only shows alongside radar (it follows radar's
  // frame), otherwise it follows the user's layer toggle
  const isLightningVisible =
    mapMode === 'lightning' ||
    (isLayerVisible(MapLayers.LIGHTNING) && (!isTimelineMode || isRadarActive));

  const enabledSatellites = useMemo(
    () => getEnabledSatellites(isLayerVisible),
    [isLayerVisible],
  );

  const mapContextValue = useMemo(
    () => ({ policy, isLayerVisible }),
    [policy, isLayerVisible],
  );

  // Toggle for 3D state change
  useEffect(() => {
    if (!map) return;
    // Adjust pitch for 3D, and reset bearing to 0 when changing 2D/3D
    map.easeTo({ pitch: is3D ? DEFAULT_3D_PITCH : 0, bearing: 0 });
    if (is3D) {
      map.touchZoomRotate.enableRotation();
    } else {
      map.touchZoomRotate.disableRotation();
    }
  }, [map, is3D]);

  const { handleMissingStyleImage } = useHandleMissingMapStyleImage(map);
  const mapIconsWithRasters = useMemo(() => getMapIconsWithRasters(), []);

  useMapEvent('styleimagemissing', handleMissingStyleImage, map, true);

  useEffect(() => {
    if (!map) return;
    WILDFIRE_MAP_RASTER_ICON_NAMES.forEach((name) => {
      if (map.hasImage(name)) {
        map.removeImage(name);
      }
    });
  }, [map]);

  useOnPixelRatioChange(
    useCallback(() => {
      if (!map) return;
      mapIconsWithRasters.forEach((icon) => {
        // Skip icons that are used as fill patterns in layer styles because
        // sometimes the patterns disappear and other times they look distorted
        // for no apparent reason.
        if (!icon.skipRerenderOnPixelRatioChange && map.hasImage(icon.name)) {
          map.removeImage(icon.name);
        }
      });
    }, [map, mapIconsWithRasters]),
  );

  // Turn off rotation using touch gestures. There is no attribute on <MapComponent> to do this, only to
  // disable BOTH zooming and rotation using touch gestures.
  const handleOnLoad = useCallback(() => {
    if (!map) return;
    map.touchZoomRotate.disableRotation(); // TODO: 3D Map - we may need to adjust this
    map.setBearing(0);
    // Needed for e2e testing - allows us to wait until mas has been loaded
    window.wdMapLoaded = true;
  }, [map]);

  // @todo remove when removing 'pin_drop_improvements' release flag
  const handleLocationChange = useCallback(
    (location: MapLocation | null): void => {
      if (!location) {
        setSelectedLocation(null);
        return;
      }

      const { lat, lng, bbox } = location;

      let place = null;
      if (!pinImprovements) {
        place = location.place;
      }

      if (!hasValidCoordinates({ lat, lng })) {
        return;
      }

      if (onLocationChange) {
        onLocationChange(location);
        map?.easeTo({ center: [lng, lat] });
        return;
      }

      if (!pinImprovements) {
        if (!place) setSelectedLocation(location);
      }

      map?.easeTo({ center: [lng, lat], zoom: LOCATION_ZOOM_LEVEL });

      if (!bbox || !onViewportChange) return;

      // https://docs.mapbox.com/help/glossary/bounding-box/
      const southWest = new LngLat(bbox[0], bbox[1]);
      const northEast = new LngLat(bbox[2], bbox[3]);
      const boundingBox = new LngLatBounds(southWest, northEast);

      onViewportChange({
        center: { lat, lng },
        zoom: 6,
        bounds: boundingBox,
      });
    },
    [
      map,
      onLocationChange,
      onViewportChange,
      setSelectedLocation,
      pinImprovements,
    ],
  );

  useEffect(() => {
    if (!map || !locationState) return;
    if (!pinImprovements) {
      window.history.replaceState({}, document.title);
      handleLocationChange(locationState);
    }
  }, [handleLocationChange, locationState, map, pinImprovements]);

  const bearing = useMapBearing() || 0;

  const handleMouseDown = useCallback(() => {
    setCursorStyle('grab');
  }, []);

  const handleMouseUp = useCallback(() => {
    setCursorStyle(cursor);
  }, [cursor]);

  const getContainerClasses = (): string => {
    if (isPhone) {
      return classes.mobileContent;
    }

    if (isDrawerEntitySelected) {
      if (searchDrawerOpen) {
        return `${classes.content} ${classes.contentWithSideDrawerOpen}`;
      }
      return `${classes.content} ${classes.contentWithSideDrawerClosed}`;
    }

    return classes.content;
  };

  const openLayersMenu = useCallback((): void => {
    openLayersDrawer('layers');
  }, [openLayersDrawer]);

  const openLegendsMenu = useCallback((): void => {
    openLayersDrawer('legend');
  }, [openLayersDrawer]);

  const isPlatformWeb = Capacitor.getPlatform() === 'web';

  const controlsBaseMarginBottom =
    !isDesktop && withSearchDrawer ? getSearchCardMinSnapPx() + 8 : 8;

  // Keep the bottom controls clear of `PersistentStatusAlert`.
  const getControlsBottom = (extraPx = 0): string =>
    `calc(${
      controlsBaseMarginBottom + extraPx
    }px + ${STATUS_ALERT_OFFSET_CSS_VALUE})`;

  const initialViewState = useMemo(
    (): Partial<ViewState> & {
      bounds?: LngLatBoundsLike;
      fitBoundsOptions?: FitBoundsOptions;
    } => ({
      latitude: center.lat,
      longitude: center.lng,
      zoom,
      maxZoom: DEFAULT_VIEW_STATE.maxZoom,
      minZoom,
      // @ts-expect-error
      bounds,
      fitBoundsOptions,
      pitch: is3D ? DEFAULT_3D_PITCH : 0,
    }),
    [bounds, center.lat, center.lng, fitBoundsOptions, is3D, minZoom, zoom],
  );

  // This adds authentication headers to the request for tiles if they are
  // targeting our backend.
  const maybeAuthenticateRequest = useAuthenticatedTileRequestTransformer();

  const ccsStyle = useMemo(
    () => ({
      ...DEFAULT_VIEW_STATE.style,
      ...style,
    }),
    [style],
  );

  return (
    <MapContextProvider value={mapContextValue}>
      <Box
        className={getContainerClasses()}
        id={MAP_DOM_ELEMENT_ID}
        ref={mapContainerElementRef}
        sx={
          isTimelineMode && !isDesktop
            ? {
                '& .mapboxgl-ctrl-top-left': {
                  top: `${TIMELINE_MOBILE_LEGEND_HEIGHT}px !important`,
                },
              }
            : undefined
        }
      >
        <Box className={classes.containerQueryContext}>
          <MapComponent
            ref={(ref) => setMap(ref?.getMap())}
            mapboxAccessToken={ENV.VITE_MAPBOX_ACCESS_TOKEN}
            onLoad={handleOnLoad}
            mapStyle={mapStyle}
            style={ccsStyle}
            reuseMaps
            id={id}
            minZoom={minZoom}
            maxZoom={DEFAULT_VIEW_STATE.maxZoom}
            // @ts-expect-error
            initialViewState={initialViewState}
            dragRotate={is3D}
            touchPitch={is3D}
            attributionControl={false}
            cursor={cursorStyle}
            interactive={interactive}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onClick={handleMapClick}
            onContextMenu={
              providesOwnContextMenu
                ? onContextMenu
                : fallbackContextMenuHandler
            }
            onTouchStart={onTouchStart}
            transformRequest={maybeAuthenticateRequest}
            terrain={
              is3D
                ? {
                    source: 'mapbox-dem',
                    exaggeration: DEFAULT_3D_EXAGGERATION,
                  }
                : undefined // TODO: 3D Map - Investigate https://github.com/visgl/react-map-gl/issues/2490
            }
          >
            <StoreDebugContextMapRef />
            <AttributionLayer />
            <MapBoxLogo
              style={{
                position: 'absolute',
                bottom: getControlsBottom(1),
                left: 16,
              }}
            />
            <PlanetLabsBasemapLayer />
            {unstructuredEvacuationsEnabled && <RegionsLayer />}

            {policy.allowLayersControl && (
              <LayersControl
                position="top-right"
                setDrawerOpen={openLayersMenu}
              />
            )}

            {policy.allowMainControls && (
              <>
                <LegendControl
                  position="top-right"
                  setDrawerOpen={openLegendsMenu}
                />

                {is3D && bearing !== 0 && (
                  <CompassControl
                    position="top-left"
                    resetMapBearing={resetMapBearing}
                  />
                )}

                {isInternalUser && (
                  <ZoomLevelControl
                    position="bottom-right"
                    style={{
                      marginRight: 16,
                      marginBottom: getControlsBottom(),
                    }}
                  />
                )}

                <ScaleControl
                  unit="imperial"
                  position="bottom-right"
                  style={{
                    marginRight: isInternalUser ? 8 : 16,
                    marginBottom: getControlsBottom(),
                  }}
                />

                <AttributionControl
                  compact
                  customAttribution={['<a href="http://www.esri.com">ESRI</a>']}
                  position="bottom-left"
                  style={{
                    marginLeft: 105, // don't overlap with the Mapbox logo
                    marginBottom: getControlsBottom(-8),
                    marginRight: 115, // don't overlap with the scale control
                    // AttributionControl has a white background regardless of theme,
                    // so use a literal dark color rather than a theme value.
                    color: '#2F2A2A',
                  }}
                />
              </>
            )}

            {policy.allowToolsControl &&
              (showMembershipProFeatures || pinImprovements) && (
                <>
                  <MapToolsControl
                    position="top-right"
                    exclude={toolsExclude}
                  />
                  <MeasureDistanceToolLayer />
                  {!providesOwnContextMenu && (
                    <MapToolsContextMenu
                      variant="menu"
                      onClose={formCtxMenuClose}
                      pointCoordinates={formContextMenuCoordinates}
                      anchorPosition={{
                        top: formContextMenuCoordinates?.mouseY ?? 0,
                        left: formContextMenuCoordinates?.mouseX ?? 0,
                      }}
                      exclude={toolsExclude}
                    />
                  )}
                </>
              )}

            {policy.allowGeolocateControl &&
              (ENV.VITE_3D_MAPS_ENABLED && !isIframe() ? (
                <ButtonGroup orientation="vertical">
                  <GeolocateControl
                    position="top-left"
                    userPosition={userPosition}
                    goToUserPosition={goToUserPosition}
                    forcedThemeMode={policy.forceControlThemeMode}
                  />
                  <MapViewDimensionControl
                    position="top-left"
                    mapViewDimension={mapLayersState.mapViewDimension}
                    setMapViewDimension={mapLayersState.setMapViewDimension}
                    forcedThemeMode={policy.forceControlThemeMode}
                  />
                </ButtonGroup>
              ) : (
                <>
                  {!isIframe() && (
                    <GeolocateControl
                      position="top-left"
                      userPosition={userPosition}
                      goToUserPosition={goToUserPosition}
                      forcedThemeMode={policy.forceControlThemeMode}
                    />
                  )}
                  {ENV.VITE_3D_MAPS_ENABLED && (
                    <MapViewDimensionControl
                      position="top-left"
                      mapViewDimension={mapLayersState.mapViewDimension}
                      setMapViewDimension={mapLayersState.setMapViewDimension}
                      forcedThemeMode={policy.forceControlThemeMode}
                    />
                  )}
                </>
              ))}

            {isPlatformWeb && policy.allowZoomControl && (
              <NavigationControl
                position="top-left"
                forcedThemeMode={policy.forceControlThemeMode}
              />
            )}

            {policy.allowTimelineControl && (
              <TimelineModeControl
                onToggle={handleTimelineToggle}
                position="top-right"
              />
            )}

            <ReporterCoverageLayer visible={!isRadarActive} />
            <RadarCoverageLayer visible={isRadarActive} />
            <FemaFloodHazardLayer
              visible={isLayerVisible(MapLayers.FEMA_FLOOD_HAZARD)}
            />
            <StructuredEvacuationsAllLayer
              visible={isLayerVisible(MapLayers.EVACUATION_ZONES)}
            />
            <ElectricRetailServiceLayer
              visible={isLayerVisible(MapLayers.ELECTRIC_RETAIL_SERVICE)}
            />
            <NWSWarningsAndAlertsLayer
              showFireWeather={isLayerVisible(MapLayers.RED_FLAG_WARNINGS)}
              showFloodWeather={isLayerVisible(MapLayers.FLOOD_WEATHER)}
              showWeaPolygons={isLayerVisible(MapLayers.WEA_ALERTS)}
              interactive={
                mapType !== MapType.FloodingForm &&
                mapType !== MapType.FloodingReportForm
              }
            />
            <PowerOutageLayer
              visible={isLayerVisible(MapLayers.POWER_OUTAGES)}
            />
            <HistoricalFirePerimetersLayer
              visible={isLayerVisible(MapLayers.HISTORICAL_FIRE_PERIMETERS)}
            />
            <ResponsibilityAreasLayer
              visible={isLayerVisible(MapLayers.RESPONSIBILITY_AREAS)}
            />
            <ActiveFirePerimetersLayer
              visible={isLayerVisible(MapLayers.ACTIVE_FIRE_PERIMETERS)}
            />
            {/* NGFS polygons render below the point-based heat detections so the
              hotspot markers stay legible on top of the filled areas. */}
            <NgfsDetectionsLayer
              visible={
                isNgfsDetectionsEnabled &&
                isLayerVisible(MapLayers.NGFS_DETECTIONS)
              }
              interactive={mapType === MapType.Main}
            />
            {isHeatDetectionsGisEnabled ? (
              <HeatDetectionsLayer
                visible={
                  isLayerVisible(MapLayers.VIIRS) ||
                  isLayerVisible(MapLayers.MODIS) ||
                  isLayerVisible(MapLayers.LANDSAT)
                }
                enabledSatellites={enabledSatellites}
              />
            ) : (
              <>
                <ViirsLayer visible={isLayerVisible(MapLayers.VIIRS)} />
                <ModisLayer visible={isLayerVisible(MapLayers.MODIS)} />
              </>
            )}
            <PurpleAirLayer visible={isLayerVisible(MapLayers.PURPLE_AIR)} />
            <LightningLayer
              allowLightningControl={policy.allowLightningControl}
              visible={showLightningLayer && isLightningVisible}
            />
            <FederalLandsLayer
              visible={isLayerVisible(MapLayers.FEDERAL_STATE_LOCAL)}
            />
            <RadioTowersLayer
              visible={isLayerVisible(MapLayers.RADIO_TOWERS)}
            />
            <TimelineLayers onClose={handleTimelineClose} />
            <ExternalGeoEventsLayer
              visible={
                isLayerVisible(MapLayers.EXTERNAL_GEO_EVENTS) && isInternalUser
              }
              interactive={mapType === MapType.Main}
            />
            {userPosition.locationEnabled &&
              hasValidCoordinates(userPosition) && (
                <UserMarker
                  lat={userPosition.lat}
                  lng={userPosition.lng}
                  heading={userPosition.az}
                />
              )}
            {!pinImprovements &&
              selectedLocation &&
              hasValidCoordinates(selectedLocation) && (
                <PlaceMarker
                  lat={selectedLocation.lat}
                  lng={selectedLocation.lng}
                  name={selectedLocation.name}
                />
              )}
            {policy.withPlaces && <PlacesLayer places={places} />}
            <DeckOverlay>
              <WindLayer visible={isLayerVisible(MapLayers.SURFACE_WIND)} />
              {/* Aircrafts layer is here so that aircraft traces are below incident markers, which
                would be in {children} below.  */}
              {isDeckGlAircraftLayerEnabled ? (
                <AircraftsLayerDeckGl
                  visible={isLayerVisible(MapLayers.FLIGHT_TRACKER)}
                />
              ) : (
                <AircraftsLayer
                  visible={isLayerVisible(MapLayers.FLIGHT_TRACKER)}
                />
              )}
            </DeckOverlay>
            {children}
            <PoiDialog />
            {policy.allowMainControls && <SupportUsDialog />}
            {isIframe() && <AppStoresDialog />}
          </MapComponent>
          {showSearch && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                zIndex: 1,
                '& .MuiButton-root': {
                  height: 32,
                  minHeight: 32,
                  py: 0,
                },
                [`@container (max-width: ${theme.breakpoints.values.sm}px)`]: {
                  right: 16,
                },
              }}
            >
              <SearchBar
                onLocationChange={handleLocationChange}
                includeGeoEvents
                searchApi={searchApi}
                disabled={searchDisabled}
                ignoreSelectedGeoEventId={searchIgnoresSelectedGeoEvent}
                embedded
              />
              {searchBarAdornment}
            </Box>
          )}
        </Box>
      </Box>
      <LayersNavControl />
      <LayersDrawer />
      <LayersGroupDrawer />
      <LocationPermissionDialog
        open={userPosition.askForPermission}
        onClose={() => userPosition.setAskForPermission(false)}
      />
    </MapContextProvider>
  );
};

const ReportMarker = (props: ReportMarkerProps): JSX.Element | null => {
  const {
    dateCreated,
    id,
    isFadable = true,
    lat: latOverride, // from incident report
    lng: lngOverride, // from incident report
    media,
    onClick,
    az: reportAz, // from incident report
    overrideCoords = false,
    maxAgeHours,
  } = props;

  // get first element if media is an array
  const relevantMedia = Array.isArray(media) ? media[0] : media;

  // media's lat and lng are preferred but the marker's lat and lng default to the incident report lat/lng
  const lat =
    overrideCoords && latOverride
      ? latOverride
      : (relevantMedia?.lat ?? latOverride ?? null);

  const lng =
    overrideCoords && lngOverride
      ? lngOverride
      : (relevantMedia?.lng ?? lngOverride ?? null);

  if (!hasValidCoordinates({ lat, lng })) {
    return null;
  }

  const az = reportAz || relevantMedia?.az || 0;

  const opacity = getOpacityForReportMarker(
    dateCreated,
    isFadable,
    maxAgeHours,
  );

  return (
    <MarkerComponent
      longitude={lng!}
      latitude={lat!}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : undefined, zIndex: 2, opacity }}
      anchor="center"
      scale={1}
    >
      <div
        style={{
          position: 'relative',
          width: ReportIcon.width,
          height: ReportIcon.height,
        }}
      >
        {az > 0 && (
          <img
            alt={`report fov ${id}`}
            src={ReportIconFov.data}
            width={ReportIconFov.width}
            height={ReportIconFov.height}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -77%) rotate(${az}deg)`,
              transformOrigin: '50% 77%',
              pointerEvents: 'none',
            }}
          />
        )}
        <img
          alt={`report ${id}`}
          src={ReportIcon.data}
          width={ReportIcon.width}
          height={ReportIcon.height}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    </MarkerComponent>
  );
};

const ReportMediaMarkers = (props: ReportMediaMarkersProps): ReactNode => {
  const {
    reports,
    isFadable,
    interactive = true,
    showObfuscation = false,
    maxAgeHours,
  } = props;
  const history = useHistory();
  const { pathname } = useLocation();

  const reportsArray = useMemo(() => {
    if (!reports) return [];
    return Array.isArray(reports) ? reports : [reports];
  }, [reports]);

  if (!reports) {
    return null;
  }

  const handleClickReport = (modelId: number, reportId: number): void => {
    const prefix = pathname.includes('incident') ? 'incident' : 'i';
    // @todo we should have a better route for reports without incidents than /null/: https://trello.com/c/6vzfqxkQ/278-create-route-for-reports-without-incidents
    history.push(`/${prefix}/${modelId}/reports/${reportId}`);
  };

  const markers = reportsArray.map<ReactElement>((report) => {
    return (
      <ReportMarker
        key={report.id}
        {...report}
        isFadable={isFadable}
        maxAgeHours={maxAgeHours}
        onClick={
          interactive
            ? () => handleClickReport(report.geoEventId, report.id)
            : undefined
        }
      />
    );
  }, []);

  return (
    <>
      {showObfuscation && (
        <ReportObfuscationLayer
          isFadable={isFadable}
          reports={reportsArray}
          sourceId="report-media-markers-obfuscation"
          maxAgeHours={maxAgeHours}
        />
      )}
      {markers}
    </>
  );
};

export const WildFireGeoEventMarker = (
  props: WildfireGeoEventMarkerProps,
): JSX.Element | null => {
  const {
    anchor = 'bottom',
    isFadable = false,
    geoEvent,
    draggable,
    onDrag,
    onDragEnd,
    opacityOverride,
  } = props;
  const { lat, lng } = geoEvent;
  const icon = useMemo(() => getWildfireGeoEventIcon(geoEvent), [geoEvent]);

  if (!lat || !lng) {
    return null;
  }

  const scale = getWildfireEventScale(geoEvent);
  const opacity =
    opacityOverride ?? getOpacityForGeoEventMarker({ geoEvent, isFadable });

  return (
    <Marker
      zIndex={GEO_EVENT_MARKER_Z_INDEX}
      position={{ lat, lng }}
      opacity={opacity}
      anchor={anchor}
      icon={icon}
      scale={scale}
      draggable={draggable}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
    />
  );
};

export const FloodingGeoEventMarker = (
  props: FloodingGeoEventMarkerProps,
): JSX.Element | null => {
  const {
    anchor = 'bottom',
    isFadable = false,
    geoEvent,
    draggable,
    onDrag,
    onDragEnd,
    opacityOverride,
  } = props;
  const { lat, lng } = geoEvent;

  if (!lat || !lng) {
    return null;
  }

  const scale = getFloodingEventScale(geoEvent);
  const opacity =
    opacityOverride ?? getOpacityForGeoEventMarker({ geoEvent, isFadable });

  return (
    <Marker
      zIndex={GEO_EVENT_MARKER_Z_INDEX}
      position={{ lat, lng }}
      opacity={opacity}
      anchor={anchor}
      icon={getFloodingEventIcon(geoEvent)}
      scale={scale}
      draggable={draggable}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
    />
  );
};

export const HazardGeoEventMarker = (
  props: HazardGeoEventMarkerProps,
): JSX.Element | null => {
  const {
    anchor = 'center',
    geoEvent,
    draggable,
    onDrag,
    onDragEnd,
    opacityOverride,
  } = props;
  const { lat, lng } = geoEvent;

  if (!lat || !lng) {
    return null;
  }

  return (
    <Marker
      zIndex={GEO_EVENT_MARKER_Z_INDEX}
      position={{ lat, lng }}
      opacity={opacityOverride}
      anchor={anchor}
      icon={getHazardEventIcon(geoEvent)}
      draggable={draggable}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
    />
  );
};

// Updating map state on zoom and map move events
const MapEvents = (props: MapEventsProps): null => {
  const { onViewportChange } = props;
  const { current: map } = useMap();

  const emitViewport = (): void => {
    onViewportChange?.({
      center: map?.getCenter(),
      zoom: map?.getZoom(),
      bounds: map?.getBounds() || undefined,
    });
  };

  useMapEvent('moveend', emitViewport, map, true);
  useMapEvent('load', emitViewport, map, true);

  return null;
};

const MemoizedMap = memo(Map) as unknown as FC<MapProps> & {
  Marker: (props: MarkerProps) => JSX.Element;
  ReportMediaMarkers: (props: ReportMediaMarkersProps) => ReactNode;
  ReportMarker: (props: ReportMarkerProps) => JSX.Element | null;
  WildFireGeoEventMarker: (
    props: WildfireGeoEventMarkerProps,
  ) => JSX.Element | null;
  FloodingGeoEventMarker: (
    props: FloodingGeoEventMarkerProps,
  ) => JSX.Element | null;
  HazardGeoEventMarker: (
    props: HazardGeoEventMarkerProps,
  ) => JSX.Element | null;
  MapEvents: (props: MapEventsProps) => null;
  // @todo remove PlaceMarker when removing 'pin_drop_improvements' release flag
  PlaceMarker: (props: PlaceMarkerProps) => JSX.Element;
  LocationMarker: (props: LocationMarkerProps) => JSX.Element | null;
  DefaultConfig: DefaultViewState & {
    center: { lat: number; lng: number };
  };
};

MemoizedMap.Marker = Marker;
MemoizedMap.ReportMediaMarkers = ReportMediaMarkers;
MemoizedMap.ReportMarker = ReportMarker;
MemoizedMap.WildFireGeoEventMarker = WildFireGeoEventMarker;
MemoizedMap.FloodingGeoEventMarker = FloodingGeoEventMarker;
MemoizedMap.HazardGeoEventMarker = HazardGeoEventMarker;
MemoizedMap.MapEvents = MapEvents;
MemoizedMap.PlaceMarker = PlaceMarker;
MemoizedMap.LocationMarker = LocationMarker;

// Export for compatibility.
MemoizedMap.DefaultConfig = {
  ...DEFAULT_VIEW_STATE,
  center: DEFAULT_LATLNG,
};

export { MemoizedMap as Map };
